import type { SWRConfiguration } from 'swr'
import type { GenerationModelType } from '../apollo/generated/graphql'
import type { PriceWithCurrency } from '../apollo/generated/graphql'
import { usePublicDynamicConfig } from '../restful/dynamicConfig'

export interface TagCategoryConfig {
  name: string
  locales?: Record<string, any>
}

export interface TagConfig {
  categories: TagCategoryConfig[]
}

type OfferItem = {
  currencies: PriceWithCurrency[]
}

type LoraTrainingPrice = {
  originalPrice?: number
  price?: number
  retrain?: number
  reuse?: number
}
export interface DynamicConstants {
  dailyClaimAmount?: number
  defaultReplacementMediaId?: string
  socialMediaWithFollowReward?: string[]
  externalUrls?: {
    discordJoin?: string
    discordSupport?: string
    reddit?: string
    twitter?: string
    /**
     * @example { "ja": "https://www.google.co.jp", "en": "https://www.google.com" }
     */
    twitterAccounts?: Record<string, string>
    instagram?: string
    tiktok?: string
    youtubeAccounts?: Record<string, string>
    naverCafe?: string
  }
  tag?: TagConfig
  controlnetMethods?: {
    method: string
    hintType: string
    demo: string
    annotateFormats: string[]
  }[]
  samplingMethods?: string[]
  loraRecommendModels: string[]
  enlargeModels?: string[]
  archivedModelStorageUserId?: string

  simpleGenerationModelIds?: string[]

  // In-house DiT model IDs for prompt helper behavior
  inHouseDitModelIds?: string[]

  // In-house MMDIT26A model IDs for prompt helper behavior
  inHouseMMDIT26AModelIds?: string[]

  commonOffer?: {
    enabled?: boolean
    prices?: {
      'membership-start-MONTH': OfferItem
      'membership-plus-MONTH': OfferItem
      'membership-premium-MONTH': OfferItem
    }
  }

  // autocomplete assets
  tagCompleteAssets?: {
    tags?: {
      json: string[]
      jsonl: string[]
    }
    relatedTag?: {
      bin: string[]
    }
  }

  // Gen panel plugins
  imageEnhancementPlugins?: {
    [pluginName: string]: {
      // I18N key with fallback
      label: string
      // Image `src`
      image: string
      // Whether to display the new label (boolean) and till when (date string)
      new?: boolean | string
      workflowName: string
      workflowVersion: string
      args?: Record<
        // Arg name
        string,
        {
          // I18N key with fallback
          label?: string
          // Default value
          value?: unknown
          // Value type
          type?: 'text' | 'radios'
          // Whether to be shown in the gen panel (user-editable)
          hidden?: boolean
          // Other options
          [key: string]: unknown
        }
      >
    }
  }

  // Model IDs of base models for model market filter
  // Format: Record< value, label_as_i18n_key >
  baseModelsForModelMarketFilter?: Record<string, string>

  userReferral?: {
    rewardCreditsPercentage?: number
  }

  // Chat Editing
  chatEditing?: {
    modelId?: string
    promptLimit?: number
    imageLimit?: number
    quickAccessPlugins?: string[]
  }

  // Payment
  payment?: {
    autoRenewableProviders?: string[]
    onerwayGooglePay?: {
      enableScope?: 'all' | 'admin'
    }
  }

  // couponSystem
  couponSystem?: {
    enabled?: boolean
  }
}

export const useDynamicConstants = (opt?: SWRConfiguration) => {
  return usePublicDynamicConfig<DynamicConstants>('constants', opt)
}

export const useEnhancementPlugins = () => {
  const { data, ...rest } = useDynamicConstants()
  return {
    data: data?.imageEnhancementPlugins,
    ...rest,
  }
}

export type DynamicDefaultParameters = Partial<{
  model: string

  negativePrompts: string
  samplingSteps: number
  samplingMethod: string
  cfgScale: number
  enlargeModel: string
}>

/**
 * `Base` is not a model name
 */
export type DynamicDefaultParametersConfig = {
  base: DynamicDefaultParameters
}
export const useDynamicDefaultParameters = () => {
  return usePublicDynamicConfig<DynamicDefaultParametersConfig>(
    'defaultParameters',
  )
}

export type DynamicTrainLoraModelItem = {
  versionId: string
  modelId: string
  usage: 'animation' | 'realistic'
  modelType:
    | typeof GenerationModelType.SdV1Model
    | typeof GenerationModelType.SdxlModel
    | typeof GenerationModelType.Dit7Model
    | typeof GenerationModelType.Mmdit26AModel
  coverUrl: string
  modelName: string
}

export type DynamicTrainLoraModels = {
  models: DynamicTrainLoraModelItem[]
  pricing?: {
    pricesByModelType?: Partial<Record<GenerationModelType, LoraTrainingPrice>>
  }
}

export const useDynamicTrainLoraModels = () => {
  return usePublicDynamicConfig<DynamicTrainLoraModels>('trainLoraModels')
}

export type DynamicGenerationModelConfig = {
  // LoRA categories for model market filter
  // Format: Record< value, label_as_i18n_key >
  loraCategories?: Record<string, string>
  /**
   * Only `color` is used by the frontend; `label` and `badge` are
   * consumed by mobile clients and kept here for type parity.
   */
  modelTypeTagColors?: Record<
    string,
    {
      label: string
      badge: string
      color: string
    }
  >
}

export function useDynamicGenerationModelConfig() {
  return usePublicDynamicConfig<DynamicGenerationModelConfig>(
    'generationModelConfig',
  )
}
